import { ALL_ACCESS_PROFILE_ROLE, CRICKET_BOWLING_COACH_PROFILE_ROLE, PermissionScopes, PERMISSION_DRIVEN_ROLES, READ_ONLY_CORE_LIBRARY_PROFILE_ROLE, TEAM_COACH_PROFILE_ROLE, } from '@strength/db-schema';
import { z } from 'zod';
export { PERMISSION_DRIVEN_ROLES };
export const zPermissionScope = z.enum(PermissionScopes);
/**
 * Whether a role is governed by the per-coach permission document rather than
 * a static route-allow list. Only `coach` and `coach-admin` are permission-driven;
 * legacy roles (`all-access`, `tablet-only`, etc.) bypass the permission system.
 */
export function isPermissionDrivenRole(role) {
    return PERMISSION_DRIVEN_ROLES.includes(role);
}
/**
 * Leaf-only reader: missing/undefined and true grant; only explicit false denies.
 * This is the core of Rule 3 (store explicit denials only) — every permission
 * check routes through this so an absent key is never read as a denial.
 */
export function isPermissionGranted(permission) {
    return permission !== false;
}
/**
 * Resolves one action leaf from a permission document by its dotted group id
 * (e.g. 'library.exercises' -> doc.library.exercises.create). An absent
 * document, group, or key yields `undefined` — granted per Rule 3 — so callers
 * pass the result straight to `isPermissionGranted`.
 */
export function permissionValue(doc, group, action) {
    const node = group
        .split('.')
        .reduce((child, key) => child && typeof child === 'object'
        ? child[key]
        : undefined, doc);
    const value = node && typeof node === 'object'
        ? node[action]
        : undefined;
    return typeof value === 'boolean' ? value : undefined;
}
const zBool = z.boolean();
const zPermissionAction = z.strictObject({
    view: zBool.optional(),
    create: zBool.optional(),
    edit: zBool.optional(),
    delete: zBool.optional(),
});
export const profilePermissionsSchema = z.strictObject({
    plan: z
        .strictObject({
        tracks: zPermissionAction
            .extend({ archive: zBool.optional() })
            .optional(),
        blocks: zPermissionAction
            .extend({ viewActuals: zBool.optional() })
            .optional(),
        phases: zPermissionAction.optional(),
    })
        .optional(),
    actuals: z
        .strictObject({ view: zBool.optional(), edit: zBool.optional() })
        .optional(),
    notes: z
        .strictObject({ view: zBool.optional(), edit: zBool.optional() })
        .optional(),
    teams: z
        .strictObject({
        profiles: z.strictObject({ view: zBool.optional() }).optional(),
        activityLog: z.strictObject({ view: zBool.optional() }).optional(),
        benchmarks: z
            .strictObject({ view: zBool.optional(), edit: zBool.optional() })
            .optional(),
        settings: z
            .strictObject({ view: zBool.optional(), edit: zBool.optional() })
            .optional(),
    })
        .optional(),
    library: z
        .strictObject({
        exercises: zPermissionAction.optional(),
        schemes: zPermissionAction.optional(),
        templates: zPermissionAction.optional(),
        tags: zPermissionAction.optional(),
        exerciseMaxConversions: zPermissionAction.optional(),
    })
        .optional(),
    tablet: z.strictObject({ view: zBool.optional() }).optional(),
});
/** Input schema for `permissions.permissionsForProfile`. */
export const permissionsForProfileInput = z.object({
    profileId: z.uuid(),
});
/** Input schema for `permissions.savePermissions`. */
export const savePermissionsInput = z.object({
    profileId: z.uuid(),
    scope: zPermissionScope,
    permissions: profilePermissionsSchema,
});
export const PERMISSION_GROUPS = [
    {
        id: 'library.exercises',
        titleKey: 'exercises',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.libraryExercises.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.libraryExercises.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.libraryExercises.edit',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.libraryExercises.delete',
                    },
                },
            },
        },
    },
    {
        id: 'library.schemes',
        titleKey: 'schemes',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.librarySchemes.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.librarySchemes.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.librarySchemes.edit',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.librarySchemes.delete',
                    },
                },
            },
        },
    },
    {
        id: 'library.templates',
        titleKey: 'templates',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.libraryTemplates.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.libraryTemplates.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.libraryTemplates.edit',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.libraryTemplates.delete',
                    },
                },
            },
        },
    },
    {
        id: 'library.tags',
        titleKey: 'tags',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.libraryTags.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.libraryTags.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.libraryTags.edit',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.libraryTags.delete',
                    },
                },
            },
        },
    },
    {
        id: 'library.exerciseMaxConversions',
        titleKey: 'maxConversions',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.libraryExerciseMaxConversions.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.libraryExerciseMaxConversions.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.libraryExerciseMaxConversions.edit',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.libraryExerciseMaxConversions.delete',
                    },
                },
            },
        },
    },
    {
        id: 'plan.tracks',
        titleKey: 'tracks',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.planTracks.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.planTracks.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.planTracks.edit',
                    },
                    archive: {
                        descriptionKey: 'permissionActionDescription.planTracks.archive',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.planTracks.delete',
                    },
                },
            },
        },
    },
    {
        id: 'plan.blocks',
        titleKey: 'blocks',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.planBlocks.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.planBlocks.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.planBlocks.edit',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.planBlocks.delete',
                    },
                    viewActuals: {
                        descriptionKey: 'permissionActionDescription.planBlocks.viewActuals',
                    },
                },
            },
        },
    },
    {
        id: 'plan.phases',
        titleKey: 'phases',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.planPhases.view',
                children: {
                    create: {
                        descriptionKey: 'permissionActionDescription.planPhases.create',
                    },
                    edit: {
                        descriptionKey: 'permissionActionDescription.planPhases.edit',
                    },
                    delete: {
                        descriptionKey: 'permissionActionDescription.planPhases.delete',
                    },
                },
            },
        },
    },
    {
        id: 'actuals',
        titleKey: 'actuals',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.actuals.view',
                children: {
                    edit: { descriptionKey: 'permissionActionDescription.actuals.edit' },
                },
            },
        },
    },
    {
        id: 'notes',
        titleKey: 'notes',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.notes.view',
                children: {
                    edit: { descriptionKey: 'permissionActionDescription.notes.edit' },
                },
            },
        },
    },
    {
        id: 'teams.profiles',
        titleKey: 'profiles',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.teamsProfiles.view',
            },
        },
    },
    {
        id: 'teams.activityLog',
        titleKey: 'activityLog',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.teamsActivityLog.view',
            },
        },
    },
    {
        id: 'teams.benchmarks',
        titleKey: 'benchmarks',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.teamsBenchmarks.view',
                children: {
                    edit: {
                        descriptionKey: 'permissionActionDescription.teamsBenchmarks.edit',
                    },
                },
            },
        },
    },
    {
        id: 'teams.settings',
        titleKey: 'settings',
        actions: {
            view: {
                descriptionKey: 'permissionActionDescription.teamsSettings.view',
                children: {
                    edit: {
                        descriptionKey: 'permissionActionDescription.teamsSettings.edit',
                    },
                },
            },
        },
    },
    {
        id: 'tablet',
        titleKey: 'tabletMode',
        actions: {
            view: { descriptionKey: 'permissionActionDescription.tablet.view' },
        },
    },
];
const fullCrud = { view: true, create: true, edit: true, delete: true };
/** Every action key of a group's node tree — the label is the key itself. */
export function groupActionRows(groupId) {
    const group = PERMISSION_GROUPS.find((g) => g.id === groupId);
    if (!group)
        return [];
    const rows = [];
    const walk = (actions, depth) => {
        for (const [action, node] of Object.entries(actions)) {
            rows.push({
                action: action,
                descriptionKey: node.descriptionKey,
                depth,
            });
            if (node.children)
                walk(node.children, depth + 1);
        }
    };
    walk(group.actions, 0);
    return rows;
}
/**
 * Recursively walks a group's action tree, emitting a dependency edge for every
 * child action: the child (`subAction`) is effective only while its parent
 * (`dependsOn`) is granted. Root actions have no parent, so they emit no edge.
 */
function dependenciesOf(actions, parent) {
    return Object.entries(actions).flatMap(([action, node]) => [
        ...(parent ? [{ subAction: action, dependsOn: parent }] : []),
        ...(node.children ? dependenciesOf(node.children, action) : []),
    ]);
}
/** Flattened action ids in display order (roots first, children nested order). */
export function permissionActions(groupId) {
    const group = PERMISSION_GROUPS.find((g) => g.id === groupId);
    if (!group)
        return [];
    const flatten = (actions) => Object.entries(actions).flatMap(([action, node]) => [
        action,
        ...(node.children ? flatten(node.children) : []),
    ]);
    return flatten(group.actions);
}
/**
 * A group's dependencies, read straight off the taxonomy's nesting. When a
 * group gains a differently-shaped gate, the taxonomy entry — not this
 * function — is where it changes.
 */
export function permissionDependencies(groupId) {
    const group = PERMISSION_GROUPS.find((g) => g.id === groupId);
    if (!group)
        return [];
    return dependenciesOf(group.actions);
}
/**
 * Rebuilds a full `ProfilePermissions` document from a resolved value per
 * group (e.g. drawer draft state). Every group passed in is written in full
 * at its dot path — this is the "fully resolved" shape a client sends on
 * save; `reduceToExplicitDenials` is what turns it into the sparse
 * denials-only shape actually persisted.
 */
export function buildPermissionsDocument(groupValues) {
    const doc = {};
    for (const group of PERMISSION_GROUPS) {
        const values = groupValues[group.id];
        if (!values)
            continue;
        const segments = group.id.split('.');
        const lastSegment = segments[segments.length - 1];
        let node = doc;
        for (const segment of segments.slice(0, -1)) {
            node[segment] = node[segment] ?? {};
            node = node[segment];
        }
        node[lastSegment] = values;
    }
    return doc;
}
/**
 * Reduces a fully-resolved permission document to the sparse, denials-only
 * shape Rule 3 requires for storage: drops any leaf that resolves to
 * granted (`true` or absent) and keeps only explicit `false` values. Empty
 * groups are dropped entirely rather than persisted as `{}`.
 */
export function reduceToExplicitDenials(doc) {
    return (pruneGrantedValues(doc) ?? {});
}
/**
 * Recursive core of `reduceToExplicitDenials`: walks the document tree and
 * drops any leaf that resolves to granted (`true` or absent), keeping only
 * explicit `false` values. Empty branches collapse to `undefined` so they're
 * omitted entirely rather than persisted as `{}`.
 */
function pruneGrantedValues(value) {
    if (typeof value === 'boolean') {
        return value ? undefined : false;
    }
    const pruned = {};
    for (const [key, child] of Object.entries(value)) {
        if (child === undefined)
            continue;
        const prunedChild = pruneGrantedValues(child);
        if (prunedChild !== undefined) {
            pruned[key] = prunedChild;
        }
    }
    return Object.keys(pruned).length > 0 ? pruned : undefined;
}
export const FULL_GRANTED_PERMISSIONS = {
    plan: {
        tracks: { ...fullCrud, archive: true },
        blocks: { ...fullCrud, viewActuals: true },
        phases: fullCrud,
    },
    actuals: { view: true, edit: true },
    notes: { view: true, edit: true },
    teams: {
        profiles: { view: true },
        activityLog: { view: true },
        benchmarks: { view: true, edit: true },
        settings: { view: true, edit: true },
    },
    library: {
        exercises: fullCrud,
        schemes: fullCrud,
        templates: fullCrud,
        tags: fullCrud,
        exerciseMaxConversions: fullCrud,
    },
    tablet: { view: true },
};
/**
 * Fully-denied permission document: every action leaf in
 * profilePermissionsSchema as an explicit `false`, derived by recursively
 * walking the schema shape so it can never drift when the permission tree
 * grows (groups, sub-groups, or new actions are picked up automatically).
 * Every leaf is an explicit false because an absent key resolves to granted
 * (the storage contract), so an empty object would be fully granted, not
 * denied. Used client-side while a permission-driven coach's document is
 * loading or failed to load — permission-gated UI must render disabled
 * rather than clickable-and-failing. This only unwraps `.optional()`
 * wrappers; if a group were ever wrapped in `.default()`, `.nullable()` or
 * `.refine()` instead, it would collapse to a single `false` leaf — the
 * key-path parity test against FULL_GRANTED_PERMISSIONS is the guard for
 * that.
 */
function denySchemaNode(schema) {
    // Unwrap `.optional()` wrappers; strict objects are still ZodObject.
    const inner = schema instanceof z.ZodOptional ? schema.unwrap() : schema;
    if (inner instanceof z.ZodObject) {
        const denied = {};
        for (const [key, child] of Object.entries(inner.shape)) {
            denied[key] = denySchemaNode(child);
        }
        return denied;
    }
    // Leaf: any non-object schema (z.boolean() or an extension of it).
    return false;
}
export const FULL_DENIED_PERMISSIONS = denySchemaNode(profilePermissionsSchema);
/**
 * Client-side permission resolution for pre-existing (legacy) roles, mirroring
 * the old `coreLibrary` split: `all-access` and `team-coach` had library
 * write, every other legacy role (and admin sessions, which have no role) was
 * read-only. Legacy roles never consult a permission document — they keep
 * this resolution until they are migrated to `coach` / `coach-admin`, so
 * their behavior is identical with the feature flag on or off.
 *
 * Two non-library exceptions preserve pre-custom-permissions client behavior:
 * - `teams.settings.edit` is denied for the restricted roles (and admin
 *   sessions): the Team Settings edit control hung off `coreLibrary.write`,
 *   which was false for them.
 * - `teams.benchmarks.edit` stays granted for every legacy role: benchmark
 *   editing was never client-gated and remains server-permitted for the
 *   restricted roles ("Benchmarks are mutable" in the route configs).
 */
export function getPermissionsForLegacyRole(role) {
    if (role === ALL_ACCESS_PROFILE_ROLE || role === TEAM_COACH_PROFILE_ROLE) {
        return FULL_GRANTED_PERMISSIONS;
    }
    const libraryReadOnly = {
        view: true,
        create: false,
        edit: false,
        delete: false,
    };
    const library = {
        exercises: { ...libraryReadOnly },
        schemes: { ...libraryReadOnly },
        templates: { ...libraryReadOnly },
        tags: { ...libraryReadOnly },
        exerciseMaxConversions: { ...libraryReadOnly },
    };
    // SC-3023: read-only-core-library deliberately retains workout template
    // management (the whitelist grants it and the drawer hides it was the bug).
    // cricket-bowling-coach inherits the same base routes.
    if (role === READ_ONLY_CORE_LIBRARY_PROFILE_ROLE ||
        role === CRICKET_BOWLING_COACH_PROFILE_ROLE) {
        library.templates = { view: true, create: true, edit: true, delete: true };
    }
    return {
        ...FULL_GRANTED_PERMISSIONS,
        library,
        teams: {
            profiles: { view: true },
            activityLog: { view: true },
            benchmarks: { view: true, edit: true },
            settings: { view: true, edit: false },
        },
    };
}
//# sourceMappingURL=index.js.map